import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { useAsyncFn } from 'react-use';
import { bugoAgencyDeliveryPopulate } from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  deleteGiftRebateSettlement,
  listGiftRebateSettlements,
  patchGiftRebateSettlement,
  settleGiftRebate,
} from '@shared/api/giftRebateSettlement/giftRebateSettlement.controller';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import {
  GiftSettlementStatus,
  SettlementPendingEntry,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { getSettlementPending } from '@shared/api/settlementPending/settlementPending.controller';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';

import { RebateHistoryDetailModal } from '../components/agencyGiftSettlement/RebateHistoryDetailModal';
import { RebateSettlementTable } from '../components/agencyGiftSettlement/RebateSettlementTable';
import { DateFilter } from '../components/giftSettlement/DateFilter';
import { PaidConfirmModal } from '../components/giftSettlement/PaidConfirmModal';
import { SettlementCreateModal } from '../components/giftSettlement/SettlementCreateModal';
import { SettlementEditModal } from '../components/giftSettlement/SettlementEditModal';
import { formatDate } from '../utils/delivery.utils';
import { exportRebateHistorySummaryExcel } from '../utils/giftSettlementHistoryExcel';
import {
  getBugoBrandId,
  getBugoBrandName,
  getBugoBrandViaMari,
  statusColor,
} from '../utils/rebate-settlement.utils';
import {
  computeRebateTotals,
  getRebateBreakdown,
  pendingEntryToRebateRow,
  sortRebateRowsByDeliveredAt,
} from '../utils/rebate-table.utils';
import { Toast } from '../components/Toast';
import { useRowSelection } from '../hooks/useRowSelection';
import { useToast } from '../hooks/useToast';
import { formatWon, ceilWon, settlementDisplayTotal } from '../utils/settlement.utils';

// ─────────────────────────────────────────────
// 상조 배송형 정산 관리 (구 GiftSettlementPage의 상조정산 탭 분리)
// 상조(bugoBrand)에 지급하는 배송형(rebate) 정산 전용 페이지.
// 배송업체(supply)는 GiftSettlementSupplyPage, 쿠폰형은 GiftSettlementAgencyCouponPage.
// ─────────────────────────────────────────────

// 라벨은 화면 계열마다 다르다(이 페이지 '정산집계' vs rebate-settlement.utils '정산예정')
// — 색상만 공용 statusColor를 쓰고 라벨은 로컬 유지 (통일은 카피 결정 필요)
const statusLabel = (s: GiftSettlementStatus) =>
  s === GiftSettlementStatus.Paid ? '지급완료' : '정산집계';

const viaMarieBadge = (viaMari: boolean | undefined) => {
  if (viaMari === undefined) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        viaMari ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {viaMari ? '마리 경유' : '가온 직접'}
    </span>
  );
};

// ───────────────────────── 상조정산(rebate) 예정 ─────────────────────────

interface PendingSectionProps {
  isAdmin: boolean;
  onSettled: () => void;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칩을 숨긴다 */
  giftAgentView?: boolean;
}

const RebatePendingSection: FC<PendingSectionProps> = ({
  isAdmin,
  onSettled,
  giftAgentView = false,
}) => {
  const toast = useToast();
  const [{ loading, value: entries }, fetchPending] = useAsyncFn(
    () => getSettlementPending('rebate', {}, LowerRole.Admin),
    [],
  );

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { selected, toggleRow, toggleGroup, clear: clearSelected } = useRowSelection();
  const [modal, setModal] = useState<{
    open: boolean;
    bugoBrandId: string;
    bugoBrandName: string;
    ids: string[];
    amount: number;
  }>({ open: false, bugoBrandId: '', bugoBrandName: '', ids: [], amount: 0 });

  const filtered = useMemo(() => {
    const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return (entries ?? []).filter((e) => {
      // GiftAgent(마리)는 마리가 상조에 지급하는 건(viaMari)만 본다
      if (giftAgentView && getBugoBrandViaMari(e.delivery) !== true) {
        return false;
      }
      if (fromT !== null || toT !== null) {
        const at = e.delivery.shipping?.deliveredAt;
        if (!at) return false;
        const t = new Date(at).getTime();
        if (fromT !== null && t < fromT) return false;
        if (toT !== null && t > toT) return false;
      }
      return true;
    });
  }, [entries, dateFrom, dateTo, giftAgentView]);

  const groups = useMemo(() => {
    const map = new Map<
      string,
      { name: string; viaMari?: boolean; entries: SettlementPendingEntry[] }
    >();
    filtered.forEach((e) => {
      const id = getBugoBrandId(e.delivery) ?? '__none__';
      const name = id === '__none__' ? '미지정' : getBugoBrandName(e.delivery);
      const viaMari = getBugoBrandViaMari(e.delivery);
      if (!map.has(id)) map.set(id, { name, viaMari, entries: [] });
      map.get(id)!.entries.push(e);
    });
    // 선택(selected)과 무관한 행 변환·정렬·합계는 여기서 1회만 계산 —
    // 체크 토글마다 전 그룹을 다시 map/sort/집계하지 않게 한다
    return Array.from(map.entries()).map(([id, g]) => {
      const rows = sortRebateRowsByDeliveredAt(g.entries.map(pendingEntryToRebateRow));
      const totals = computeRebateTotals(rows);
      const firstBd = g.entries[0]
        ? getRebateBreakdown(g.entries[0].delivery)
        : undefined;
      const feeRate = firstBd && !firstBd.isRebateMethod ? firstBd.feeRate : undefined;
      const groupTotal = g.entries.reduce((s, e) => s + e.preview.lineAmount, 0);
      return { id, ...g, rows, totals, feeRate, groupTotal };
    });
  }, [filtered]);

  const handleSettle = async ({
    year,
    month,
    memo,
    overrideAmount,
  }: {
    year: number;
    month: number;
    memo: string;
    overrideAmount: number;
  }) => {
    try {
      // 같은 (상조, 정산월) 정산이 이미 있으면: Draft는 합치고, Paid는 차단·안내
      const existing = await listGiftRebateSettlements(
        { bugoBrand: modal.bugoBrandId, year, month },
        [],
        {},
        LowerRole.Admin,
      );
      const dup = existing[0];
      if (dup) {
        if (dup.status === GiftSettlementStatus.Paid) {
          toast.show(
            `이미 지급완료된 ${year}년 ${month}월 정산이 있습니다. 수정하려면 먼저 Draft로 되돌리세요.`,
          );
          return;
        }
        // 기존 월 정산에 합칠 땐 전체 라인이 재계산되므로 override는 적용하지 않는다.
        await patchGiftRebateSettlement(dup._id, { addDeliveryIds: modal.ids });
        toast.show(`${year}년 ${month}월 정산에 ${modal.ids.length}건 추가 완료`);
      } else {
        const result = await settleGiftRebate({
          year,
          month,
          target: { bugoBrand: modal.bugoBrandId },
          giftDeliveryIds: modal.ids,
          memo: memo || undefined,
        });
        const skipped = result.skipped.length;
        if (skipped > 0) {
          const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join(
            ', ',
          );
          toast.show(
            `${result.includedCount}건 집행 완료. ${skipped}건 제외 (${reasons})`,
          );
        } else {
          toast.show(`${result.includedCount}건 집행 완료`);
        }
        // 실제 정산액을 직접 입력했고 계산값과 다르면 그 금액으로 동결(신규 정산만)
        if (overrideAmount !== ceilWon(modal.amount)) {
          await patchGiftRebateSettlement(result.settlement._id, {
            overrideAmount: true,
            totalAmount: overrideAmount,
          });
        }
      }
      setModal((m) => ({ ...m, open: false }));
      clearSelected();
      fetchPending();
      onSettled();
    } catch (e: any) {
      const msg = e?.response?.data?.message || '집행 실패';
      toast.show(`오류: ${msg}`);
      throw e;
    }
  };

  if (loading)
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );

  return (
    <div>
      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFrom={setDateFrom}
        onTo={setDateTo}
      />

      {groups.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          미정산 내역이 없습니다.
        </div>
      ) : (
        groups.map((group) => {
          // 선택 종속 값만 렌더에서 계산 — 행/합계/요율은 groups useMemo에서 동결
          const groupIds = group.entries.map((e) => e.delivery._id);
          const selectedInGroup = groupIds.filter((id) => selected.has(id));
          const allGroupSel =
            groupIds.length > 0 && groupIds.every((id) => selected.has(id));
          const { groupTotal } = group;
          const selectedTotal = group.entries
            .filter((e) => selected.has(e.delivery._id))
            .reduce((s, e) => s + e.preview.lineAmount, 0);

          return (
            <RebateSettlementTable
              key={group.id}
              rows={group.rows}
              totals={group.totals}
              emptyMessage="미정산 내역이 없습니다."
              fixedLayout
              showFootnote={false}
              feeRate={group.feeRate}
              selection={
                isAdmin ? { selectedKeys: selected, onToggleRow: toggleRow } : undefined
              }
              header={
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={allGroupSel}
                        onChange={() => toggleGroup(group.entries)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    )}
                    <span className="font-semibold text-gray-800">{group.name}</span>
                    {!giftAgentView && viaMarieBadge(group.viaMari)}
                    <span className="text-xs text-gray-400">
                      {group.entries.length}건 · {formatWon(ceilWon(groupTotal))}
                    </span>
                  </div>
                  {isAdmin && (
                    <button
                      disabled={selectedInGroup.length === 0}
                      onClick={() =>
                        setModal({
                          open: true,
                          bugoBrandId: group.id,
                          bugoBrandName: group.name,
                          ids: selectedInGroup,
                          amount: selectedTotal,
                        })
                      }
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      선택 집행 ({selectedInGroup.length}건
                      {selectedInGroup.length > 0 ? ` · ${formatWon(selectedTotal)}` : ''}
                      )
                    </button>
                  )}
                </div>
              }
            />
          );
        })
      )}

      <SettlementCreateModal
        open={modal.open}
        targetName={modal.bugoBrandName}
        count={modal.ids.length}
        amount={modal.amount}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={handleSettle}
      />
      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};

// ───────────────────────── 상조정산(rebate) 내역 ─────────────────────────

interface HistorySectionProps {
  isAdmin: boolean;
  refreshTrigger: number;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칼럼을 숨긴다 */
  giftAgentView?: boolean;
}

const STATUS_FILTERS: { key: 'all' | GiftSettlementStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: GiftSettlementStatus.Draft, label: '정산집계' },
  { key: GiftSettlementStatus.Paid, label: '지급완료' },
];

const RebateHistorySection: FC<HistorySectionProps> = ({
  isAdmin,
  refreshTrigger,
  giftAgentView = false,
}) => {
  const toast = useToast();
  // GiftAgent(마리)는 /admin이 아니라 /gift-agent 읽기 전용 prefix로 조회한다(백엔드가 viaMari 건만 스코프).
  const role = giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin;
  const [{ loading, value: items }, fetchHistory] = useAsyncFn(
    () =>
      listGiftRebateSettlements(
        {},
        [
          { path: 'bugoBrand', select: 'bugoAgency giftAccountConfig' },
          // 상세보기 모달의 배송 메타(구매자/고인명/발인일/지도사/배송일) 표시용
          { path: 'lines.giftDelivery', populate: bugoAgencyDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | GiftSettlementStatus>('all');
  const [editTarget, setEditTarget] = useState<GiftRebateSettlement | null>(null);
  // 상세보기 모달에 띄울 정산 문서 (null이면 닫힘)
  const [detailTarget, setDetailTarget] = useState<GiftRebateSettlement | null>(null);
  const [paidModal, setPaidModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    count: number;
    amount: number;
  }>({ open: false, id: '', name: '', count: 0, amount: 0 });

  const handlePatch = async (
    id: string,
    status: GiftSettlementStatus,
    label: string,
    executedAt?: string,
    override?: { totalAmount: number },
  ): Promise<boolean> => {
    setActionLoading(id);
    try {
      await patchGiftRebateSettlement(id, {
        status,
        ...(executedAt ? { executedAt } : {}),
        ...(override ? { overrideAmount: true, totalAmount: override.totalAmount } : {}),
      });
      toast.show(`${label} 완료`);
      fetchHistory();
      return true;
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || label + ' 실패'}`);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    let all = (items ?? []) as GiftRebateSettlement[];
    // GiftAgent(마리)는 마리가 지급하는 상조정산(viaMari)만 본다
    if (giftAgentView) {
      all = all.filter(
        (s) =>
          typeof s.bugoBrand === 'object' &&
          s.bugoBrand.giftAccountConfig?.viaMari === true,
      );
    }
    return statusFilter === 'all' ? all : all.filter((s) => s.status === statusFilter);
  }, [items, statusFilter, giftAgentView]);
  const summary = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((sum, s) => sum + settlementDisplayTotal(s), 0),
    }),
    [filtered],
  );
  const editBugoBrandId =
    editTarget &&
    (typeof editTarget.bugoBrand === 'object'
      ? editTarget.bugoBrand._id
      : editTarget.bugoBrand);
  const editBugoBrandName =
    editTarget && typeof editTarget.bugoBrand === 'object'
      ? editTarget.bugoBrand.bugoAgency?.corpName ||
        editTarget.bugoBrand.bugoAgency?.nickName ||
        '상조정산'
      : '상조정산';

  if (loading)
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  if (!items?.length)
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );

  return (
    <div>
      {/* 상태 필터 + 요약 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                statusFilter === f.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {summary.count}건 · 합계{' '}
            <span className="font-semibold text-rose-700">
              {formatWon(summary.total)}
            </span>
          </p>
          <button
            onClick={() => exportRebateHistorySummaryExcel(filtered, giftAgentView)}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2.5 text-left">상조</th>
              {!giftAgentView && <th className="px-3 py-2.5 text-left">경유</th>}
              <th className="px-3 py-2.5 text-left">정산월</th>
              <th className="px-3 py-2.5 text-left">상태</th>
              <th className="px-3 py-2.5 text-right">건수</th>
              <th className="px-3 py-2.5 text-right">정산금액</th>
              <th className="px-3 py-2.5 text-left">지급일</th>
              <th className="px-3 py-2.5 text-left">메모</th>
              <th className="px-3 py-2.5 text-center">상세</th>
              {isAdmin && <th className="px-3 py-2.5 text-right">관리</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={(isAdmin ? 10 : 9) - (giftAgentView ? 1 : 0)}
                  className="px-3 py-10 text-center text-gray-400"
                >
                  해당 상태의 정산이 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const bb = s.bugoBrand;
              const bbName =
                typeof bb === 'object'
                  ? bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id
                  : bb || '-';
              const viaMari =
                typeof bb === 'object' ? bb.giftAccountConfig?.viaMari : undefined;
              const isPaid = s.status === GiftSettlementStatus.Paid;
              const isActing = actionLoading === s._id;

              return (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                    {bbName}
                  </td>
                  {!giftAgentView && (
                    <td className="px-3 py-3">{viaMarieBadge(viaMari)}</td>
                  )}
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(
                        s.status,
                      )}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.lines.length}건
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                    {formatWon(settlementDisplayTotal(s))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {s.executedAt ? formatDate(s.executedAt) : '-'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-3 text-xs text-gray-500">
                    {s.memo || '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setDetailTarget(s)}
                      className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      상세보기
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setEditTarget(s)}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          수정
                        </button>
                        {!isPaid && (
                          <button
                            disabled={isActing}
                            onClick={() =>
                              setPaidModal({
                                open: true,
                                id: s._id,
                                name: bbName,
                                count: s.lines.length,
                                amount: s.totalAmount,
                              })
                            }
                            className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {isActing ? '처리 중...' : '지급확정'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailTarget && (
        <RebateHistoryDetailModal
          settlement={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}

      <PaidConfirmModal
        open={paidModal.open}
        targetName={paidModal.name}
        count={paidModal.count}
        amount={paidModal.amount}
        onClose={() => setPaidModal((m) => ({ ...m, open: false }))}
        onConfirm={async (executedAt, paidAmount) => {
          // 입력한 실제 지급액을 그대로 동결(override)해 확정한다 (재계산·반올림 없음 — 화면 금액 = 지급액).
          const ok = await handlePatch(
            paidModal.id,
            GiftSettlementStatus.Paid,
            'Paid 확정',
            executedAt,
            { totalAmount: paidAmount },
          );
          if (ok) setPaidModal((m) => ({ ...m, open: false }));
        }}
      />
      {editTarget && editBugoBrandId && (
        <SettlementEditModal
          open
          purpose="rebate"
          settlement={editTarget}
          targetName={editBugoBrandName}
          fetchEligible={() =>
            getSettlementPending(
              'rebate',
              { bugoBrand: editBugoBrandId },
              LowerRole.Admin,
            )
          }
          patch={patchGiftRebateSettlement}
          remove={deleteGiftRebateSettlement}
          onClose={() => setEditTarget(null)}
          onSaved={fetchHistory}
        />
      )}
      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};

// ───────────────────────── 페이지 ─────────────────────────

export const GiftSettlementAgencyDeliveryPage: FC =
  function GiftSettlementAgencyDeliveryPage() {
    const { isAdmin, isGiftAgent } = useAuth();
    const canWrite = isAdmin() && !isGiftAgent();
    // GiftAgent(마리) 조회 뷰: 마리 정산건만 노출 + 경유 칩/칼럼 숨김
    const giftAgentView = !isAdmin() && isGiftAgent();

    // 집행 후 내역 섹션 강제 리프레시
    const [historyRefresh, setHistoryRefresh] = useState(0);
    const handleSettled = useCallback(() => {
      setHistoryRefresh((n) => n + 1);
    }, []);

    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">상조 배송형 정산 관리</h1>
            <p className="mt-1 text-sm text-gray-500">
              상조 배송형(화환·답례품 배송) 정산 조회
              {canWrite ? ' · 집행 · 수정 · 확정' : ''}
            </p>
          </div>

          {/* 미정산 내역 (상단) */}
          <section className="mb-10">
            <h2 className="mb-4 text-lg font-bold text-gray-800">미정산 내역</h2>
            <RebatePendingSection
              isAdmin={canWrite}
              onSettled={handleSettled}
              giftAgentView={giftAgentView}
            />
          </section>

          {/* 정산 완료 내역 (하단) */}
          <section>
            <h2 className="mb-4 text-lg font-bold text-gray-800">정산 완료 내역</h2>
            <RebateHistorySection
              isAdmin={canWrite}
              refreshTrigger={historyRefresh}
              giftAgentView={giftAgentView}
            />
          </section>
        </div>
      </div>
    );
  };
